import React from 'react';
import { Card, Table, Tag, Typography, message, Input, Space, Button, Drawer, Select, Row } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import { listExternalDocumentsForInspector } from '../../external-documents/services/external-documentService';
import type { Page } from '../../../types/Page';

const documentTypeMap: Record<string, string> = {
  LEGISLATION: 'Mevzuat',
  STANDARD: 'Standart',
  GUIDE: 'Rehber',
  NORM: 'Norm',
};

const ExternalDocumentsPage: React.FC = () => {
  const [data, setData] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const [total, setTotal] = React.useState<number>(0);
  const [searchInput, setSearchInput] = React.useState<string>('');
  const [search, setSearch] = React.useState<string>('');
  const [documentType, setDocumentType] = React.useState<string | undefined>(undefined);
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [pDocType, setPDocType] = React.useState<string | undefined>(undefined);

  const applySearch = React.useMemo(
    () =>
      debounce((val: string) => {
        setPage(1);
        setSearch(val);
      }, 300),
    [],
  );
  React.useEffect(() => () => applySearch.cancel(), [applySearch]);

  const normalizeRows = React.useCallback((list: any[]) =>
    list.map((item: any) => ({
      id: item?.id,
      documentType: item?.documentType,
      documentTypeName: documentTypeMap[item?.documentType] ?? item?.documentType ?? '-',
      documentNo: item?.documentNumber ?? item?.documentNo ?? '',
      documentName: item?.documentName ?? '',
      fileUrl: item?.documentFilePath ?? item?.fileUrl ?? '',
    })), []);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await listExternalDocumentsForInspector({
        search: search.trim(),
        documentType: documentType,
        page: page - 1,
        size: pageSize,
        sort: 'documentName,asc',
      });
      if (Array.isArray(response)) {
        const normalizedList = normalizeRows(response);
        const start = (page - 1) * pageSize;
        setTotal(normalizedList.length);
        setData(normalizedList.slice(start, start + pageSize));
      } else {
        const pageResponse = response as Page<any>;
        const content = Array.isArray(pageResponse?.content) ? pageResponse.content : [];
        const normalizedList = normalizeRows(content);
        setData(normalizedList);
        setTotal(pageResponse?.totalElements ?? normalizedList.length);
      }
    } catch (error) {
      console.error('InspectorExternalDocuments fetch failed', error);
      message.error('Dokümanlar yüklenemedi.');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [documentType, normalizeRows, page, pageSize, search]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  React.useEffect(() => {
    if (filterOpen) {
      setPDocType(documentType);
    }
  }, [filterOpen, documentType]);

  const columns = [
    {
      title: 'Doküman Türü',
      dataIndex: 'documentTypeName',
      render: (value: string) => {
        const label = value ?? '-';
        return label ? <Tag color="processing">{label}</Tag> : '-';
      },
    },
    { title: 'Doküman No', dataIndex: 'documentNo' },
    { title: 'Doküman Adı', dataIndex: 'documentName' },
    {
      title: 'Dosya',
      dataIndex: 'fileUrl',
      render: (value: string) =>
        value ? (
          <a href={value} target="_blank" rel="noreferrer">
            Görüntüle
          </a>
        ) : (
          '-'
        ),
    },
  ];

  return (
    <Card
      className="br-30px kontrolx-card"
      title={(
        <Row align='bottom'>
          <Typography.Title level={4} style={{ margin: 0 }}>Dış Kaynaklı Dokümanlar</Typography.Title>
          <Typography.Text type='secondary' className='ms-5' style={{ fontWeight: 500 }}>
            <strong>({total})</strong> kayıt listelendi
          </Typography.Text>
        </Row>
      )}
      extra={(
        <Space>
          <Input.Search
            allowClear
            placeholder='Ara'
            prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
            className='d-input d-input-form d-input-filter'
            style={{ width: 240 }}
            value={searchInput}
            onChange={(e) => {
              const val = e.target.value || '';
              setSearchInput(val);
              if (val.length >= 2) {
                applySearch(val);
              } else if (val.length === 0) {
                applySearch('');
              }
            }}
            onSearch={(val: string) => {
              const next = (val || '').trim();
              setSearchInput(next);
              if (next.length >= 2 || next.length === 0) {
                setPage(1);
                setSearch(next);
              }
            }}
          />
          <Button type='primary' onClick={() => setFilterOpen(true)}>Filtrele</Button>
        </Space>
      )}
    >
      <Table
        rowKey={(record: any) => record?.id ?? `${record?.documentNo}-${record?.documentName}`}
        dataSource={data}
        columns={columns as any}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (nextPage: number, nextPageSize: number) => {
            if (nextPageSize !== pageSize) {
              setPageSize(nextPageSize);
              setPage(1);
              return;
            }
            setPage(nextPage);
          },
        }}
      />
      <Drawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        destroyOnClose
        width={360}
        title='Filtre'
      >
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          <div>
            <Typography.Text>Doküman Türü</Typography.Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 6 }}
              value={pDocType}
              onChange={(v) => setPDocType(v || undefined)}
              options={Object.keys(documentTypeMap).map((key) => ({ value: key, label: documentTypeMap[key] }))}
            />
          </div>
          <Row justify='end' gutter={12}>
            <Space>
              <Button
                onClick={() => {
                  setPDocType(undefined);
                  setPage(1);
                  setDocumentType(undefined);
                }}
              >
                Temizle
              </Button>
              <Button
                type='primary'
                onClick={() => {
                  setPage(1);
                  setDocumentType(pDocType);
                  setFilterOpen(false);
                }}
              >
                Filtrele
              </Button>
            </Space>
          </Row>
        </Space>
      </Drawer>
    </Card>
  );
};

export default ExternalDocumentsPage;
